import type { ReactNode } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  resolveBrandWikiId,
  resolveEnterpriseWikiId,
  setBrandToolAccess,
  setFetchOn,
  setKbWikiAccess,
  setMaterialsAccess,
  type CapabilityAccess,
  type CapabilityDraft,
} from "@/lib/hermes-capability-bindings";
import type { BrandSiteStudioPanelLock } from "@/lib/hermes-brand-site-studio";
import { CapabilityFold, type CapabilityBrandOption } from "./CapabilityAccordion";

type AccessOption<T> = { value: T; label: string };

const MATERIALS_OPTIONS: AccessOption<CapabilityAccess | null>[] = [
  { value: null, label: "不选" },
  { value: "read", label: "只读" },
  { value: "write", label: "完全控制" },
];

const BRAND_TOOL_OPTIONS: AccessOption<CapabilityAccess | null>[] = [
  { value: null, label: "不选" },
  { value: "read", label: "只读" },
  { value: "write", label: "完全访问" },
];

const FETCH_OPTIONS: AccessOption<boolean>[] = [
  { value: false, label: "关" },
  { value: true, label: "开" },
];

type HermesCapabilityPanelProps = {
  accountName: string;
  brands: CapabilityBrandOption[];
  draft: CapabilityDraft;
  onDraftChange: (draft: CapabilityDraft) => void;
  enterpriseWikiId?: number | null;
  brandWikiByBrandId?: Record<number, number>;
  journeyBrandIds?: number[];
  brandSiteBrandIds?: number[];
  brandSiteBusyBrandId?: number | null;
  disabled?: boolean;
  /** 设计工作室：必选项不可降级，仍可加码其它只读 */
  studioLock?: BrandSiteStudioPanelLock | null;
  /** 标题栏关闭按钮；由外层 Popover/Dialog 传入 */
  onClose?: () => void;
  className?: string;
};

function materialsAccess(
  draft: CapabilityDraft,
  target: { scope: "enterprise" } | { scope: "brand"; brand_id: number },
): CapabilityAccess | null {
  const row = draft.materials.find((item) =>
    target.scope === "enterprise"
      ? item.scope === "enterprise"
      : item.scope === "brand" && item.brand_id === target.brand_id,
  );
  return row?.access ?? null;
}

function kbAccess(draft: CapabilityDraft, wikiId: number | null | undefined): CapabilityAccess | null {
  if (wikiId == null) return null;
  return draft.kbBindings.find((row) => row.wiki_id === wikiId)?.access ?? null;
}

function brandToolAccess(
  draft: CapabilityDraft,
  tool: "journey" | "brandSite",
  brandId: number,
): CapabilityAccess | null {
  return draft[tool].find((row) => row.brand_id === brandId)?.access ?? null;
}

function brandFoldCount(draft: CapabilityDraft, brandId: number, wikiId?: number): number {
  let count = 0;
  if (draft.materials.some((row) => row.scope === "brand" && row.brand_id === brandId)) count += 1;
  if (wikiId != null && draft.kbBindings.some((row) => row.wiki_id === wikiId)) count += 1;
  if (draft.journey.some((row) => row.brand_id === brandId)) count += 1;
  if (draft.brandSite.some((row) => row.brand_id === brandId)) count += 1;
  return count;
}

function AccessButtons<T extends string | boolean | null>({
  label,
  options,
  value,
  disabled = false,
  lockedValues = [],
  onChange,
}: {
  label: string;
  options: AccessOption<T>[];
  value: T;
  disabled?: boolean;
  lockedValues?: T[];
  onChange: (value: T) => void;
}) {
  const columns = options.length === 2 ? "grid-cols-2" : "grid-cols-3";
  return (
    <div className={cn("grid gap-1", columns)} role="radiogroup" aria-label={label}>
      {options.map((option) => {
        const selected = value === option.value;
        const optionDisabled = disabled || lockedValues.includes(option.value);
        return (
          <button
            key={String(option.value)}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={optionDisabled}
            className={cn(
              "rounded-md border px-1.5 py-1.5 text-[11px] font-medium transition-colors",
              selected
                ? "border-primary bg-primary text-primary-foreground shadow-sm"
                : "border-transparent bg-muted/60 text-foreground/70 hover:bg-muted hover:text-foreground",
              optionDisabled && !selected && "cursor-not-allowed opacity-60",
              optionDisabled && selected && "cursor-not-allowed opacity-90",
            )}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function ToolRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <section className="flex items-center justify-between gap-3 px-3 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </div>
      <div className="w-[11.5rem] shrink-0">{children}</div>
    </section>
  );
}

/** 「+ 能力」面板：企业一块（联网访问 → 资源库 → 知识库）+ 各品牌手风琴。 */
export function HermesCapabilityPanel({
  accountName,
  brands,
  draft,
  onDraftChange,
  enterpriseWikiId = null,
  brandWikiByBrandId = {},
  journeyBrandIds = [],
  brandSiteBrandIds = [],
  brandSiteBusyBrandId = null,
  disabled = false,
  studioLock = null,
  onClose,
  className,
}: HermesCapabilityPanelProps) {
  const studioBrandId = studioLock?.brandId ?? null;
  const journeyIdSet = new Set(journeyBrandIds);
  const brandSiteIdSet = new Set(brandSiteBrandIds);
  // catalog 只用于标题/映射；档位跟 draft，缺 catalog 时回退已有 wiki_id
  const resolvedEnterpriseWikiId = resolveEnterpriseWikiId(
    draft,
    enterpriseWikiId,
    brandWikiByBrandId,
  );
  const enterpriseCount =
    (draft.fetchOn ? 1 : 0) +
    (materialsAccess(draft, { scope: "enterprise" }) ? 1 : 0) +
    (kbAccess(draft, resolvedEnterpriseWikiId) ? 1 : 0);

  return (
    <div className={cn("w-[22rem] max-w-[calc(100vw-2rem)]", className)}>
      <div className="flex items-start justify-between gap-2 border-b border-border bg-muted/30 px-3 py-2.5">
        <div className="min-w-0">
          <h3 className="text-sm font-medium">本轮能力</h3>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {studioLock
              ? "本站必备能力已锁定；可额外开启其它站点或库的只读作参考"
              : "先选范围，再开工具；点空白处或右上角关闭"}
          </p>
        </div>
        {onClose ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="size-7 shrink-0 text-muted-foreground hover:text-foreground"
            aria-label="关闭能力面板"
            onClick={onClose}
          >
            <X className="size-4" aria-hidden />
          </Button>
        ) : null}
      </div>

      <div className="max-h-[min(60vh,24rem)] space-y-2 overflow-y-auto overscroll-contain p-3">
        <CapabilityFold title={`企业 · ${accountName}`} count={enterpriseCount} defaultOpen>
          <ToolRow label="联网访问" hint="抓取公开网页">
            <AccessButtons
              label="联网访问"
              options={FETCH_OPTIONS}
              value={draft.fetchOn}
              disabled={disabled}
              lockedValues={studioLock ? [false] : []}
              onChange={(fetchOn) => onDraftChange(setFetchOn(draft, fetchOn))}
            />
          </ToolRow>
          <ToolRow label="资源库">
            <AccessButtons
              label="企业资源库"
              options={MATERIALS_OPTIONS}
              value={materialsAccess(draft, { scope: "enterprise" })}
              disabled={disabled}
              lockedValues={studioLock ? [null] : []}
              onChange={(access) =>
                onDraftChange(setMaterialsAccess(draft, { scope: "enterprise" }, access))
              }
            />
          </ToolRow>
          <ToolRow label="知识库">
            <AccessButtons
              label="企业知识库"
              options={MATERIALS_OPTIONS}
              value={kbAccess(draft, resolvedEnterpriseWikiId)}
              disabled={disabled}
              lockedValues={
                resolvedEnterpriseWikiId == null
                  ? ["read", "write"]
                  : studioLock
                    ? [null]
                    : []
              }
              onChange={(access) => {
                if (resolvedEnterpriseWikiId == null) return;
                onDraftChange(setKbWikiAccess(draft, resolvedEnterpriseWikiId, access));
              }}
            />
          </ToolRow>
        </CapabilityFold>

        {brands.map((brand) => {
          const wikiId = resolveBrandWikiId(draft, brandWikiByBrandId[brand.id]);
          const showJourney = journeyIdSet.has(brand.id);
          const showSite = brandSiteIdSet.has(brand.id);
          const siteBusy = brandSiteBusyBrandId === brand.id;
          return (
            <CapabilityFold
              key={brand.id}
              title={`品牌 · ${brand.name}`}
              count={brandFoldCount(draft, brand.id, wikiId)}
              defaultOpen={brandFoldCount(draft, brand.id, wikiId) > 0}
            >
              <ToolRow label="资源库">
                <AccessButtons
                  label={`${brand.name}资源库`}
                  options={MATERIALS_OPTIONS}
                  value={materialsAccess(draft, { scope: "brand", brand_id: brand.id })}
                  disabled={disabled}
                  lockedValues={studioLock && brand.id === studioBrandId ? [null] : []}
                  onChange={(access) =>
                    onDraftChange(
                      setMaterialsAccess(draft, { scope: "brand", brand_id: brand.id }, access),
                    )
                  }
                />
              </ToolRow>
              <ToolRow label="知识库">
                <AccessButtons
                  label={`${brand.name}知识库`}
                  options={MATERIALS_OPTIONS}
                  value={kbAccess(draft, wikiId)}
                  disabled={disabled}
                  lockedValues={
                    wikiId == null
                      ? ["read", "write"]
                      : studioLock && brand.id === studioBrandId
                        ? [null]
                        : []
                  }
                  onChange={(access) => {
                    if (wikiId == null) return;
                    onDraftChange(setKbWikiAccess(draft, wikiId, access));
                  }}
                />
              </ToolRow>
              {showJourney ? (
                <ToolRow label="客户旅程">
                  <AccessButtons
                    label={`${brand.name}客户旅程`}
                    options={BRAND_TOOL_OPTIONS}
                    value={brandToolAccess(draft, "journey", brand.id)}
                    disabled={disabled}
                    onChange={(access) =>
                      onDraftChange(setBrandToolAccess(draft, "journey", brand.id, access))
                    }
                  />
                </ToolRow>
              ) : null}
              {showSite ? (
                <ToolRow label="AI 官网">
                  <AccessButtons
                    label={`${brand.name}AI 官网`}
                    options={BRAND_TOOL_OPTIONS}
                    value={brandToolAccess(draft, "brandSite", brand.id)}
                    disabled={disabled || siteBusy}
                    lockedValues={
                      studioLock && brand.id === studioBrandId
                        ? [null, "read"]
                        : studioLock
                          ? ["write"]
                          : []
                    }
                    onChange={(access) =>
                      onDraftChange(setBrandToolAccess(draft, "brandSite", brand.id, access))
                    }
                  />
                </ToolRow>
              ) : null}
            </CapabilityFold>
          );
        })}
      </div>
    </div>
  );
}
